import { ConfigProvider, ThemeConfig } from "antd";
import { useGetLanguageQuery } from "api/TenantSlug/Language/hooks.ts";
import { useGetUserQuery } from "api/TenantSlug/User/hooks.ts";
import { sessionAtom } from "atoms/sessionAtom.ts";
import { tenantAtom } from "atoms/tenantAtom.ts";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { ReactNode, useEffect, useRef, useState, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { UserSettingKeyEnum } from "types/enums.ts";
import { setCurrencyLocale } from "utils/currencyLocale.ts";

import theme from "../../theme.json";

import type { Locale } from "antd/es/locale";

dayjs.extend(updateLocale);

// Deep merge function for theme objects
function deepMerge(target: any, source: any): any {
    const output = { ...target };
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach((key) => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    output[key] = source[key];
                } else {
                    output[key] = deepMerge(target[key], source[key]);
                }
            } else {
                output[key] = source[key];
            }
        });
    }
    return output;
}

function isObject(item: any): boolean {
    return item && typeof item === "object" && !Array.isArray(item);
}

interface AntdConfigProviderProps {
    children: ReactNode;
}

export function AntdConfigProvider(props: AntdConfigProviderProps) {
    const { children } = props;
    const { t, i18n } = usePaldockTranslation("tables.ui.pagination");
    const [locale, setLocale] = useState<Locale | undefined>();
    const sessionState = useRecoilValue(sessionAtom);
    const tenantState = useRecoilValue(tenantAtom);
    const authenticatedUserId = sessionState.user?.id;
    const currentTenantUserQuery = useGetUserQuery(
        authenticatedUserId ?? "",
        { filter: { include: "tenantSettings" } },
        {
            enabled: Boolean(tenantState.slug_name && authenticatedUserId),
        }
    );
    const userLanguageSettingId =
        currentTenantUserQuery.data?.user?.tenantSettingsKeyValues?.[UserSettingKeyEnum.Language];
    const getLanguageQuery = useGetLanguageQuery(userLanguageSettingId ? String(userLanguageSettingId) : "", {
        enabled: Boolean(tenantState.slug_name && userLanguageSettingId),
    });

    // Normalize language from BCP-47 tags (e.g., "en-US" -> "en", "cs-CZ" -> "cs")
    const currentLanguage = (i18n.resolvedLanguage || i18n.language).split("-")[0];
    const isMountedRef = useRef(true);

    // Dynamically load the locale based on current language
    useEffect(() => {
        isMountedRef.current = true;

        const loadLocale = async () => {
            try {
                // Load Ant Design locale
                let localeModule;
                switch (currentLanguage) {
                    case "cs":
                        localeModule = await import("antd/locale/cs_CZ");
                        break;
                    case "en":
                        localeModule = await import("antd/locale/en_US");
                        break;
                    // Add more cases for future locales:
                    // case 'de':
                    //     localeModule = await import("antd/locale/de_DE");
                    //     break;
                    // case 'fr':
                    //     localeModule = await import("antd/locale/fr_FR");
                    //     break;
                    default:
                        localeModule = await import("antd/locale/en_US");
                }

                if (!isMountedRef.current) return;
                setLocale(localeModule.default);

                // Load dayjs locale
                switch (currentLanguage) {
                    case "cs":
                        await import("dayjs/locale/cs");
                        break;
                    case "en":
                        await import("dayjs/locale/en");
                        break;
                    // Add more cases for future locales:
                    // case 'de':
                    //     await import("dayjs/locale/de");
                    //     break;
                    // case 'fr':
                    //     await import("dayjs/locale/fr");
                    //     break;
                    default:
                        await import("dayjs/locale/en");
                }

                if (!isMountedRef.current) return;
                dayjs.locale(currentLanguage);
            } catch (error) {
                console.warn(`Failed to load locale for ${currentLanguage}, falling back to English`, error);

                if (!isMountedRef.current) return;

                const enModule = await import("antd/locale/en_US");
                setLocale(enModule.default);
                await import("dayjs/locale/en");
                dayjs.locale("en");
            }
        };

        loadLocale();

        return () => {
            isMountedRef.current = false;
        };
    }, [currentLanguage]);

    useEffect(() => {
        if (!tenantState.slug_name) {
            setCurrencyLocale(undefined);
            return;
        }

        setCurrencyLocale(getLanguageQuery.data?.language?.iso2);
    }, [getLanguageQuery.data?.language?.iso2, tenantState.slug_name]);

    const customProps: ThemeConfig = {
        token: {
            // Set default control height to "middle" size (32px)
            // small: 24px, middle: 32px, large: 40px
            controlHeight: 32,
        },
        components: {
            /*Table: {
                headerSplitColor: 'red',
            },*/
            /* Segmented: {
                borderRadius: 6,
                itemSelectedBg: theme.token.colorPrimary,
                itemSelectedColor: theme.token.colorTextLightSolid,
                itemColor: theme.token.colorText,
                trackBg: theme.token.colorBgContainer,
                trackPadding: 0,
            }, */
        },
    };

    // Create custom locale with our translations
    const customLocale = locale
        ? {
              ...locale,
              Pagination: {
                  ...locale.Pagination,
                  items_per_page: t("items_per_page"),
                  jump_to: t("jump_to"),
                  jump_to_confirm: t("jump_to_confirm"),
                  page: t("page"),
                  prev_page: t("prev_page"),
                  next_page: t("next_page"),
                  prev_5: t("prev_5"),
                  next_5: t("next_5"),
                  prev_3: t("prev_3"),
                  next_3: t("next_3"),
                  page_size: t("page_size"),
              },
          }
        : undefined;

    // Use deep merge to properly combine theme configurations
    const mergedTheme = useMemo(() => {
        return deepMerge(theme, customProps);
    }, []);

    return (
        <ConfigProvider theme={mergedTheme} locale={customLocale}>
            {children}
        </ConfigProvider>
    );
}
